import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { Plus, Shield, Trash2 } from "lucide-react";

const ROLES = ["super_admin", "content_manager", "marketing", "support"];

export default function AdminUsersScreen() {
  const [admins, setAdmins] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "support" });

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admins");
      setAdmins(data.admins || []);
    } catch (e) { toast.error("Super admin only"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/admins", form);
      toast.success("Admin created");
      setShowAdd(false);
      setForm({ email: "", name: "", password: "", role: "support" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const updateRole = async (admin_id, role) => {
    try { await adminApi.put(`/admins/${admin_id}/role`, { role }); toast.success("Role updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const toggleActive = async (a) => {
    try { await adminApi.put(`/admins/${a.admin_id}/active`, { active: !a.active }); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete admin ${a.email}?`)) return;
    try { await adminApi.delete(`/admins/${a.admin_id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-shadow-green">Admin Users</h1>
          <p className="text-muted-soft text-sm mt-1">Manage admin accounts and roles.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="add-admin">
          <Plus size={14} /> Invite admin
        </button>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-wider text-muted-soft border-b border-white/5">
          <div className="col-span-4">Admin</div>
          <div className="col-span-3">Role</div>
          <div className="col-span-2">Last login</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1" />
        </div>
        {admins.length === 0 ? (
          <p className="p-10 text-center text-soft text-sm">No admins yet.</p>
        ) : admins.map((a) => (
          <div key={a.admin_id} className="grid grid-cols-12 px-4 py-3 items-center text-sm border-b border-white/3 last:border-b-0" data-testid={`admin-${a.admin_id}`}>
            <div className="col-span-4">
              <p className="font-semibold truncate">{a.name}</p>
              <p className="text-[11px] text-muted-soft truncate">{a.email}</p>
            </div>
            <div className="col-span-3">
              <select
                className="bg-transparent text-sm border border-white/15 rounded-lg px-2 py-1.5 hover:border-[#4CAF50]/50 transition"
                value={a.role}
                onChange={(e) => updateRole(a.admin_id, e.target.value)}
                data-testid={`role-${a.admin_id}`}
              >
                {ROLES.map((r) => <option key={r} value={r} style={{ background: "#0A0A0A" }}>{r}</option>)}
              </select>
            </div>
            <div className="col-span-2 text-[11px] text-muted-soft">
              {a.last_login ? new Date(a.last_login).toLocaleDateString() : "Never"}
            </div>
            <div className="col-span-2">
              <button
                onClick={() => toggleActive(a)}
                className={`text-xs font-semibold px-3 py-1 rounded-full ${a.active ? "bg-[rgba(76,175,80,0.18)] text-[#4CAF50] border border-[rgba(76,175,80,0.4)]" : "bg-white/8 text-white/50 border border-white/15"}`}
                data-testid={`toggle-${a.admin_id}`}
              >
                {a.active ? "Active" : "Disabled"}
              </button>
            </div>
            <div className="col-span-1 flex justify-end">
              <button onClick={() => del(a)} className="text-white/40 hover:text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="glass-card-strong p-6 w-full max-w-md">
            <div className="flex items-center gap-2 mb-4"><Shield size={16} className="text-[#4CAF50]" /><h2 className="text-lg font-bold">Invite new admin</h2></div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-soft">Name</label>
                <input className="glass-input mt-1" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name" />
              </div>
              <div>
                <label className="text-xs text-muted-soft">Email</label>
                <input type="email" className="glass-input mt-1" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email" />
              </div>
              <div>
                <label className="text-xs text-muted-soft">Password (min 8)</label>
                <input type="password" minLength={8} className="glass-input mt-1" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password" />
              </div>
              <div>
                <label className="text-xs text-muted-soft">Role</label>
                <select className="glass-input mt-1" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="admin-role">
                  {ROLES.map((r) => <option key={r} value={r} style={{ background: "#0A0A0A" }}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-outline flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="admin-submit">Create</button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
